import { escapeHtml, fill, t, type PrintSheetHtml } from '@bms2/shared';
import { reportCaughtInBrowser } from '../../shell/systemErrors';
import { apiFetch } from './api';

/**
 * 🔒 A signed Service Report opens on its frozen sheet (the owner's 4a; decision
 * 3962; T2293): the PDF Complete the Visit froze, at the version it wrote. The
 * document frame draws printer HTML, so each page of the frozen PDF is drawn here
 * as a picture of that page and handed to the frame as one `img` per page, sized
 * in the page's own inches. 🔨 Nothing is rendered again from the template: a later
 * template or job edit cannot change what the page shows as signed.
 */
export interface FrozenSheet {
  sheet: PrintSheetHtml;
  layout: { page: { widthInches: number; heightInches: number } };
}

/** 🔨 Twice the PDF's own resolution, so the picture stays sharp at the frame's larger zoom steps. */
const DRAW_SCALE = 2;
/** The PDF's unit, the point, is a seventy-second of an inch. */
const POINTS_PER_INCH = 72;

const inches = (points: number): string => `${(points / POINTS_PER_INCH).toFixed(4)}in`;

/** The frame's HTML for pages already drawn: each page one picture at its own size, stacked with no gap, as the printer stacks sheets. */
export function frozenSheetHtml(pages: readonly { image: string; widthPoints: number; heightPoints: number }[]): string {
  const images = pages.map((page, index) => `<img src="${page.image}" alt="${escapeHtml(fill(t('document.sheet.pageAlt'), { page: String(index + 1), pages: String(pages.length) }))}" style="display:block;width:${inches(page.widthPoints)};height:${inches(page.heightPoints)}">`).join('');
  return `<!doctype html><html><head><meta charset="utf-8"><style>html,body{margin:0;padding:0;background:white}</style></head><body>${images}</body></html>`;
}

/**
 * Reads the frozen version's bytes through the API and draws each page. Answers
 * null where the bytes cannot be read or drawn, which opens the report on Details
 * with a line pointing at Print (decision 3962).
 */
export async function drawFrozenSheet(url: string, fetcher: (url: string) => Promise<Response> = apiFetch): Promise<FrozenSheet | null> {
  try {
    const response = await fetcher(url);
    // A refused or failed read is a System error row like a thrown one, and the report opens on Details (T2396).
    if (!response.ok) throw new Error(`The frozen file at ${url} answered ${response.status}.`);
    const bytes = new Uint8Array(await response.arrayBuffer());
    // 🔨 Loaded on first use, so the reader travels in its own chunk and no other page pays for it.
    const pdfjs = await import('pdfjs-dist');
    const worker = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
    pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
    const doc = await pdfjs.getDocument({ data: bytes, isEvalSupported: false }).promise;
    try {
      const pages: { image: string; widthPoints: number; heightPoints: number }[] = [];
      for (let number = 1; number <= doc.numPages; number += 1) {
        const page = await doc.getPage(number);
        const own = page.getViewport({ scale: 1 });
        const viewport = page.getViewport({ scale: DRAW_SCALE });
        const canvas = document.createElement('canvas');
        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);
        await page.render({ canvas, viewport }).promise;
        pages.push({ image: canvas.toDataURL('image/png'), widthPoints: own.width, heightPoints: own.height });
        page.cleanup();
      }
      const first = pages[0];
      if (first === undefined) return null;
      return {
        sheet: { html: frozenSheetHtml(pages), pageCount: pages.length },
        layout: { page: { widthInches: first.widthPoints / POINTS_PER_INCH, heightInches: first.heightPoints / POINTS_PER_INCH } },
      };
    } finally {
      void doc.destroy();
    }
  } catch (error) {
    // A read or a drawing that fails is a System error row; the page opens on Details with its line pointing at Print.
    reportCaughtInBrowser('records.frozenSheet.draw', error);
    return null;
  }
}

/** 🔒 The frame while the frozen sheet's pages are drawn (decision 3962): one page of paper with one line on it, so the report opens on its sheet and not on Details for a moment. */
export function frozenSheetWaiting(line: string): PrintSheetHtml {
  return {
    html: `<!doctype html><html><head><meta charset="utf-8"><style>html,body{margin:0;background:white;font-family:sans-serif;color:dimgray}p{margin:0;padding:1in;font-size:12pt}</style></head><body><p data-frozen-sheet-waiting="true">${escapeHtml(line)}</p></body></html>`,
    pageCount: 1,
  };
}
